import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from './config';
import { 
  PieChart, Pie, Cell, 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer 
} from 'recharts';

function Dashboard() {
  const [stats, setStats] = useState({
    totalCalls: 0,
    allocatedCalls: 0,
    unallocatedCalls: 0,
    pendingCalls: 0,
    totalASCs: 0
  });
  
  const [recentActivity, setRecentActivity] = useState([]);
  const [allocationTrend, setAllocationTrend] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      // Fetch each endpoint separately
      const [statsRes, recentRes, trendRes, dailyRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/dashboard/recent`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/dashboard/trend`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/dashboard/daily`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
  
      setStats(statsRes.data || {
        totalCalls: 0,
        allocatedCalls: 0,
        unallocatedCalls: 0,
        pendingCalls: 0,
        totalASCs: 0
      });
      
      setRecentActivity(Array.isArray(recentRes.data) ? recentRes.data : []);
      setAllocationTrend(Array.isArray(trendRes.data) ? trendRes.data : []);
      setDailyTrend(Array.isArray(dailyRes.data) ? dailyRes.data : []);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#007AFF', '#34C759', '#FF9500', '#FF3B30', '#AF52DE', '#5AC8FA'];

  if (loading) {
    return (
      <div className="premium-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="fade-in">
      <h1 className="page-title">Dashboard</h1>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ color: '#007AFF' }}>📞</div>
          <div className="stat-title">Total Calls</div>
          <div className="stat-value">{stats.totalCalls}</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{ color: '#34C759' }}>✅</div>
          <div className="stat-title">Allocated</div>
          <div className="stat-value">{stats.allocatedCalls}</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{ color: '#FF9500' }}>⏳</div>
          <div className="stat-title">Unallocated</div>
          <div className="stat-value">{stats.unallocatedCalls}</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon" style={{ color: '#AF52DE' }}>🏢</div>
          <div className="stat-title">Active ASCs</div>
          <div className="stat-value">{stats.totalASCs}</div>
        </div>
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Allocation by Step */}
        <div className="chart-container">
          <h3>Allocation by Step</h3>
          {allocationTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={allocationTrend}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  dataKey="value"
                >
                  {allocationTrend.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data">No data available</div>
          )}
        </div>

        {/* Daily Trend */}
        <div className="chart-container">
          <h3>Daily Allocation Trend</h3>
          {dailyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--system-separator)" />
                <XAxis dataKey="date" stroke="var(--system-secondary-label)" />
                <YAxis stroke="var(--system-secondary-label)" />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="allocated" stroke="#007AFF" strokeWidth={2} />
                <Line type="monotone" dataKey="completed" stroke="#34C759" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data">No data available</div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="premium-table-container" style={{ marginTop: '24px' }}>
        <h3>Recent Allocations</h3>
        {recentActivity.length > 0 ? (
          <table className="premium-table">
            <thead>
              <tr>
                <th>Job No.</th>
                <th>ASC Name</th>
                <th>Step</th>
                <th>CRM Status</th>
                <th>Date</th>
                <th>Email Status</th>
              </tr>
            </thead>
            <tbody>
            {recentActivity.map((item, index) => (
              <tr key={index}>
                <td><strong>{item.job_no}</strong></td>
                <td>{item.asc_name}</td>
                <td><span className="premium-badge">Step {item.allocation_step}</span></td>
                <td>
                  <span className={`premium-badge ${item.crm_status === 'Closed' ? 'success' : 'warning'}`}>
                    {item.crm_status || 'Pending'}
                  </span>
                </td>
                <td>{new Date(item.allocated_at).toLocaleDateString()}</td>
                <td>
                  <span className={`premium-badge ${item.email_sent ? 'success' : 'warning'}`}>
                    {item.email_sent ? '✓ Email Sent' : '⏳ Pending'}
                  </span>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        ) : (
          <div className="no-data">No recent activity</div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;